/** Hand-rolled SVG charts (no chart library). Every chart is role="img" with a text summary as its name, and the
 * pages always show the same numbers in a table next to it (never chart-only, never hover-only). */
import { fmtNum } from "./time";

export type XY = { x: number; y: number };

function extent(vals: number[]): [number, number] {
  if (vals.length === 0) return [0, 1];
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }
  return [lo, hi];
}

/** Path points scaled into a box (exported for tests). */
export function scalePoints(points: XY[], w: number, h: number, pad = 2): XY[] {
  const [x0, x1] = extent(points.map((p) => p.x));
  const [y0, y1] = extent(points.map((p) => p.y));
  return points.map((p) => ({
    x: pad + ((p.x - x0) / (x1 - x0)) * (w - 2 * pad),
    y: h - pad - ((p.y - y0) / (y1 - y0)) * (h - 2 * pad),
  }));
}

const poly = (pts: XY[]) => pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

export function Sparkline({ values, label, width = 120, height = 28, testId }: {
  values: number[];
  label: string;
  width?: number;
  height?: number;
  testId?: string;
}) {
  const pts = scalePoints(values.map((y, x) => ({ x, y })), width, height);
  return (
    <svg className="ws7-spark" role="img" aria-label={label} width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      data-testid={testId}>
      {pts.length > 1 && <polyline className="line" points={poly(pts)} />}
      {pts.length === 1 && pts[0] && <circle className="dot" cx={pts[0].x} cy={pts[0].y} r={2} />}
    </svg>
  );
}

export function LineChart({ points, label, width = 640, height = 200, fmtX, fmtY = fmtNum, testId }: {
  points: XY[];
  label: string;
  width?: number;
  height?: number;
  fmtX: (x: number) => string;
  fmtY?: (y: number) => string;
  testId?: string;
}) {
  const left = 56;
  const bottom = 22;
  const innerW = width - left - 8;
  const innerH = height - bottom - 8;
  const pts = scalePoints(points, innerW, innerH, 4).map((p) => ({ x: p.x + left, y: p.y + 8 }));
  const ys = points.map((p) => p.y);
  const xs = points.map((p) => p.x);
  return (
    <svg className="ws7-chart" role="img" aria-label={label} width={width} height={height}
      viewBox={`0 0 ${width} ${height}`} data-testid={testId}>
      <line className="axis" x1={left} y1={8} x2={left} y2={8 + innerH} />
      <line className="axis" x1={left} y1={8 + innerH} x2={left + innerW} y2={8 + innerH} />
      {points.length > 0 && (
        <>
          <text x={left - 4} y={14} textAnchor="end">{fmtY(Math.max(...ys))}</text>
          <text x={left - 4} y={8 + innerH} textAnchor="end">{fmtY(Math.min(...ys))}</text>
          <text x={left} y={height - 6}>{fmtX(Math.min(...xs))}</text>
          <text x={left + innerW} y={height - 6} textAnchor="end">{fmtX(Math.max(...xs))}</text>
        </>
      )}
      {pts.length > 1 && <polyline className="line" points={poly(pts)} />}
      {pts.map((p, i) => <circle key={i} className="dot" cx={p.x} cy={p.y} r={2.5} />)}
    </svg>
  );
}

export function BarChart({ rows, label, width = 640, fmt = fmtNum, testId }: {
  rows: { label: string; value: number }[];
  label: string;
  width?: number;
  fmt?: (v: number) => string;
  testId?: string;
}) {
  const rowH = 20;
  const labelW = 170;
  const valueW = 80;
  const height = Math.max(rowH, rows.length * rowH) + 4;
  const max = Math.max(1, ...rows.map((r) => r.value));
  const barW = width - labelW - valueW;
  return (
    <svg className="ws7-chart" role="img" aria-label={label} width={width} height={height}
      viewBox={`0 0 ${width} ${height}`} data-testid={testId}>
      {rows.map((r, i) => (
        <g key={`${r.label}-${i}`} transform={`translate(0 ${i * rowH + 2})`}>
          <text x={labelW - 6} y={14} textAnchor="end">{r.label.length > 26 ? `${r.label.slice(0, 25)}…` : r.label}</text>
          <rect className="bar" x={labelW} y={3} height={rowH - 6} width={Math.max(1, (Math.max(0, r.value) / max) * barW)} />
          <text x={labelW + Math.max(1, (Math.max(0, r.value) / max) * barW) + 4} y={14}>{fmt(r.value)}</text>
        </g>
      ))}
    </svg>
  );
}
